import React, { useState } from 'react';
import {
  Layers,
  Plus,
  ArrowRight,
  MapPin,
  Globe,
  Link2,
  CheckSquare,
  MoreVertical,
  FolderTree,
  Repeat,
  ShieldCheck,
  Trash2
} from 'lucide-react';
import { OnPageSeoProject, AuditInstance, KeywordResearchProject } from '../../types';
import { Button } from '../UI/Button';

interface OnPageProjectListProps {
  projects: OnPageSeoProject[];
  audits: AuditInstance[];
  kwProjects?: KeywordResearchProject[];
  onSelectProject: (id: string) => void;
  onCreateProject: (name: string, url: string, location: string, linkedAuditId?: string, linkedKeywordId?: string) => void;
  onDeleteProject: (id: string) => void;
}

export const OnPageProjectList: React.FC<OnPageProjectListProps> = ({
  projects,
  audits,
  kwProjects = [],
  onSelectProject,
  onCreateProject,
  onDeleteProject,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sourceMode, setSourceMode] = useState<'audit' | 'keyword' | 'new'>('audit');
  const [selectedAuditId, setSelectedAuditId] = useState<string>(audits[0]?.id || '');
  const [selectedKwId, setSelectedKwId] = useState<string>(kwProjects[0]?.id || '');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [loc, setLoc] = useState('Sydney');
  const [searchQuery, setSearchQuery] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const handleAuditSelect = (auditId: string) => {
    setSelectedAuditId(auditId);
    const found = audits.find(a => a.id === auditId);
    if (found) {
      setName(found.clientName);
      setUrl(found.websiteUrl);
    }
  };

  const handleKwSelect = (kwId: string) => {
    setSelectedKwId(kwId);
    const found = kwProjects.find(p => p.id === kwId);
    if (found) {
      setName(found.clientName);
      setUrl(found.websiteUrl);
      if (found.businessProfile?.primaryLocation) {
        setLoc(found.businessProfile.primaryLocation);
      }
    }
  };

  const handleOpenModal = () => {
    if (audits.length > 0) {
      setSourceMode('audit');
      handleAuditSelect(audits[0].id);
    } else if (kwProjects.length > 0) {
      setSourceMode('keyword');
      handleKwSelect(kwProjects[0].id);
    } else {
      setSourceMode('new');
    }
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !url.trim()) return;

    const linkedAuditId = sourceMode === 'audit' ? selectedAuditId : undefined;
    const linkedKeywordId = sourceMode === 'keyword' ? selectedKwId : undefined;

    onCreateProject(name.trim(), url.trim(), loc.trim(), linkedAuditId, linkedKeywordId);
    setName('');
    setUrl('');
    setIsModalOpen(false);
  };

  // Aggregated Stats
  const totalProjects = projects.length;
  const totalReviewedChecklistItems = projects.reduce((acc, p) => {
    const list = Object.values(p.checklist || {});
    return acc + list.filter(item => item.isReviewed).length;
  }, 0);
  const totalChecklistItems = projects.reduce((acc, p) => {
    return acc + Object.keys(p.checklist || {}).length;
  }, 0);
  const overallChecklistPct = totalChecklistItems > 0 ? Math.round((totalReviewedChecklistItems / totalChecklistItems) * 100) : 0;
  const totalRedirectsConfigured = projects.reduce((acc, p) => acc + (p.permalinkAudits?.length || 0), 0);

  const filteredProjects = projects.filter(p => {
    const q = searchQuery.toLowerCase();
    return p.clientName.toLowerCase().includes(q) || p.websiteUrl.toLowerCase().includes(q) || p.primaryLocation.toLowerCase().includes(q);
  });

  return (
    <div className="animate-fade-in" style={{ maxWidth: '1280px', margin: '0 auto', padding: '32px 24px 80px 24px' }}>
      
      {/* 1. HERO HEADER */}
      <div
        style={{
          background: '#090e1a',
          border: '1px solid #1e293b',
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '24px',
        }}
      >
        <div style={{ flex: '1 1 560px' }}>
          {/* Metadata Badges */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '10px', fontWeight: 800, color: '#f43f5e', background: 'rgba(244, 63, 94, 0.12)', border: '1px solid rgba(244, 63, 94, 0.3)', padding: '3px 8px', borderRadius: '6px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              ZYNEX NINJA PIPELINE
            </span>
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#38bdf8', background: 'rgba(2, 132, 199, 0.15)', border: '1px solid rgba(56, 189, 248, 0.3)', padding: '3px 8px', borderRadius: '6px', textTransform: 'uppercase' }}>
              MODULE 4 · ON-PAGE SEO IMPLEMENTATION
            </span>
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#10b981', background: 'rgba(16, 185, 129, 0.12)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '3px 8px', borderRadius: '6px', textTransform: 'uppercase' }}>
              WALKTHROUGH & CHECKLIST ENGINE
            </span>
          </div>

          <h1 style={{ fontSize: '30px', color: '#ffffff', fontWeight: 800, margin: '0 0 10px 0', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
            On-Page SEO Implementation & Review Engine
          </h1>

          <p style={{ fontSize: '14.5px', color: '#94a3b8', margin: 0, maxWidth: '680px', lineHeight: 1.6 }}>
            Execute practical on-page architectural optimizations: configure WordPress service categories, establish 1-click menu navigation, scale location page content with H1/H2 search volume rules, audit clean permalinks with 301 redirects, and verify 100% exact NAP consistency for local Google trust.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-end' }}>
          <Button
            size="lg"
            variant="primary"
            icon={<Plus size={18} />}
            onClick={handleOpenModal}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 700,
              boxShadow: '0 4px 20px rgba(2, 132, 199, 0.4)',
            }}
          >
            New On-Page Plan +
          </Button>

          <div style={{ fontSize: '12px', color: '#64748b' }}>
            Part of the unified Audit → Keyword Research → On-Page SEO Pipeline
          </div>
        </div>
      </div>

      {/* 2. STAT CARDS ROW */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '18px', marginBottom: '28px' }}>
        <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '14px', padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px' }}>ACTIVE PLANS</span>
            <Layers size={18} color="#38bdf8" />
          </div>
          <div style={{ fontSize: '28px', fontWeight: 900, color: '#ffffff', fontFamily: 'var(--font-mono)' }}>
            {totalProjects}
          </div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
            Client On-Page Projects
          </div>
        </div>

        <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '14px', padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px' }}>CHECKLIST REVIEWED</span>
            <CheckSquare size={18} color="#10b981" />
          </div>
          <div style={{ fontSize: '28px', fontWeight: 900, color: '#10b981', fontFamily: 'var(--font-mono)' }}>
            {overallChecklistPct}%
          </div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
            {totalReviewedChecklistItems} of {totalChecklistItems} items reviewed
          </div>
        </div>

        <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '14px', padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px' }}>PERMALINKS & 301s</span>
            <Repeat size={18} color="#f59e0b" />
          </div>
          <div style={{ fontSize: '28px', fontWeight: 900, color: '#ffffff', fontFamily: 'var(--font-mono)' }}>
            {totalRedirectsConfigured}
          </div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
            Mapped Redirection Rules
          </div>
        </div>

        <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '14px', padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px' }}>PIPELINE READINESS</span>
            <ShieldCheck size={18} color="#a855f7" />
          </div>
          <div style={{ fontSize: '28px', fontWeight: 900, color: '#a855f7', fontFamily: 'var(--font-mono)' }}>
            Ready
          </div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
            Connected with Audit & KW
          </div>
        </div>
      </div>

      {/* 3. PROJECT LIST SECTION */}
      <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '16px', padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '14px' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', margin: '0 0 4px 0' }}>
              Client On-Page Plans
            </h2>
            <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>
              Select a client to launch their walkthrough sections and sign off the implementation checklist.
            </p>
          </div>

          <div style={{ width: '280px' }}>
            <input
              type="text"
              placeholder="Search by client, URL or city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                background: '#040711',
                border: '1px solid #1e293b',
                borderRadius: '8px',
                padding: '8px 14px',
                fontSize: '13px',
                color: '#ffffff',
                outline: 'none',
              }}
            />
          </div>
        </div>

        {/* Project Grid */}
        {filteredProjects.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', border: '1px dashed #1e293b', borderRadius: '12px' }}>
            <FolderTree size={40} color="#475569" style={{ margin: '0 auto 16px auto' }} />
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>
              No On-Page Plans Found
            </h3>
            <p style={{ fontSize: '13px', color: '#94a3b8', maxWidth: '440px', margin: '0 auto 20px auto' }}>
              Create an On-Page SEO plan for a client or import client data directly from an existing Audit or Keyword Research project.
            </p>
            <Button size="md" variant="primary" icon={<Plus size={16} />} onClick={handleOpenModal}>
              Create First On-Page Plan
            </Button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '18px' }}>
            {filteredProjects.map((project) => {
              const checklistItems = Object.values(project.checklist || {});
              const reviewedCount = checklistItems.filter(i => i.isReviewed).length;
              const totalCount = checklistItems.length;
              const pct = totalCount > 0 ? Math.round((reviewedCount / totalCount) * 100) : 0;
              const isMenuOpen = openMenuId === project.id;

              return (
                <div
                  key={project.id}
                  style={{
                    background: '#040711',
                    border: '1px solid #1e293b',
                    borderRadius: '14px',
                    padding: '22px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div>
                    {/* Top Row: Client Name & Menu */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', margin: 0 }}>
                            {project.clientName}
                          </h3>
                          <span
                            style={{
                              fontSize: '10px',
                              fontWeight: 800,
                              color: pct === 100 ? '#10b981' : '#38bdf8',
                              background: pct === 100 ? 'rgba(16, 185, 129, 0.15)' : 'rgba(2, 132, 199, 0.15)',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              textTransform: 'uppercase',
                            }}
                          >
                            {pct === 100 ? 'Reviewed' : 'In Progress'}
                          </span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#94a3b8' }}>
                          <Globe size={13} color="#64748b" />
                          <span style={{ fontFamily: 'var(--font-mono)' }}>{project.websiteUrl}</span>
                        </div>
                      </div>

                      {/* Dropdown Menu */}
                      <div style={{ position: 'relative' }}>
                        <button
                          onClick={() => setOpenMenuId(isMenuOpen ? null : project.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#64748b',
                            cursor: 'pointer',
                            padding: '4px',
                            borderRadius: '6px',
                          }}
                        >
                          <MoreVertical size={16} />
                        </button>

                        {isMenuOpen && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '28px',
                              right: '0',
                              background: '#090e1a',
                              border: '1px solid #1e293b',
                              borderRadius: '8px',
                              padding: '6px',
                              width: '140px',
                              zIndex: 10,
                              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                            }}
                          >
                            <button
                              onClick={() => {
                                setOpenMenuId(null);
                                if (window.confirm(`Delete On-Page plan for "${project.clientName}"?`)) {
                                  onDeleteProject(project.id);
                                }
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                width: '100%',
                                padding: '8px 10px',
                                background: 'none',
                                border: 'none',
                                color: '#f43f5e',
                                fontSize: '12px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                borderRadius: '6px',
                                textAlign: 'left',
                              }}
                            >
                              <Trash2 size={13} />
                              <span>Delete Plan</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Metadata Badges */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '16px', fontSize: '11.5px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#94a3b8', background: 'rgba(30, 41, 59, 0.4)', padding: '3px 8px', borderRadius: '6px' }}>
                        <MapPin size={12} color="#38bdf8" />
                        <span>{project.primaryLocation}</span>
                      </span>

                      <span style={{ color: '#38bdf8', background: 'rgba(2, 132, 199, 0.1)', padding: '3px 8px', borderRadius: '6px', fontWeight: 600 }}>
                        {project.locationModel === 'multi_location' ? 'Multi-Location Hub' : 'Single Location'}
                      </span>

                      {project.linkedAuditId && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#10b981', background: 'rgba(16, 185, 129, 0.1)', padding: '3px 8px', borderRadius: '6px', fontWeight: 600 }}>
                          <Link2 size={11} />
                          <span>Audit Linked</span>
                        </span>
                      )}

                      {project.linkedKeywordProjectId && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#a855f7', background: 'rgba(168, 85, 247, 0.1)', padding: '3px 8px', borderRadius: '6px', fontWeight: 600 }}>
                          <Link2 size={11} />
                          <span>KW Research Linked</span>
                        </span>
                      )}
                    </div>

                    {/* Checklist Progress Bar */}
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                        <span style={{ color: '#94a3b8' }}>Review Checklist Completed</span>
                        <span style={{ color: pct === 100 ? '#10b981' : '#38bdf8', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
                          {reviewedCount} / {totalCount} ({pct}%)
                        </span>
                      </div>
                      <div style={{ width: '100%', height: '6px', background: '#1e293b', borderRadius: '3px', overflow: 'hidden' }}>
                        <div
                          style={{
                            width: `${pct}%`,
                            height: '100%',
                            background: pct === 100 ? '#10b981' : 'linear-gradient(90deg, #0284c7 0%, #38bdf8 100%)',
                            borderRadius: '3px',
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Launch Workstation Button */}
                  <Button
                    size="md"
                    variant="primary"
                    icon={<ArrowRight size={16} />}
                    onClick={() => onSelectProject(project.id)}
                    style={{
                      width: '100%',
                      justifyContent: 'center',
                      fontWeight: 700,
                      boxShadow: '0 2px 10px rgba(2, 132, 199, 0.25)',
                    }}
                  >
                    Open Walkthrough & Checklist →
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 4. CREATE NEW ON-PAGE PLAN MODAL */}
      {isModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: '20px',
          }}
        >
          <div
            style={{
              background: '#090e1a',
              border: '1px solid #1e293b',
              borderRadius: '16px',
              padding: '32px',
              width: '100%',
              maxWidth: '540px',
              boxShadow: '0 20px 50px rgba(0,0,0,0.7)',
            }}
          >
            <h3 style={{ fontSize: '20px', fontWeight: 800, color: '#ffffff', margin: '0 0 8px 0' }}>
              Create New On-Page SEO Plan
            </h3>
            <p style={{ fontSize: '13px', color: '#94a3b8', margin: '0 0 20px 0' }}>
              Link to an existing client audit or keyword research project to automatically pull business information.
            </p>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Source Mode Tabs */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '8px' }}>
                  DATA SOURCE
                </label>
                <div style={{ display: 'flex', gap: '8px', background: '#040711', padding: '4px', borderRadius: '8px', border: '1px solid #1e293b' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setSourceMode('audit');
                      if (audits[0]) handleAuditSelect(audits[0].id);
                    }}
                    style={{
                      flex: 1,
                      padding: '8px',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      background: sourceMode === 'audit' ? '#0284c7' : 'transparent',
                      color: sourceMode === 'audit' ? '#ffffff' : '#94a3b8',
                    }}
                  >
                    From Audit ({audits.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSourceMode('keyword');
                      if (kwProjects[0]) handleKwSelect(kwProjects[0].id);
                    }}
                    style={{
                      flex: 1,
                      padding: '8px',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      background: sourceMode === 'keyword' ? '#0284c7' : 'transparent',
                      color: sourceMode === 'keyword' ? '#ffffff' : '#94a3b8',
                    }}
                  >
                    From Keyword Project ({kwProjects.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSourceMode('new');
                      setName('');
                      setUrl('');
                    }}
                    style={{
                      flex: 1,
                      padding: '8px',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      background: sourceMode === 'new' ? '#0284c7' : 'transparent',
                      color: sourceMode === 'new' ? '#ffffff' : '#94a3b8',
                    }}
                  >
                    Manual New
                  </button>
                </div>
              </div>

              {/* Audit selector if audit mode */}
              {sourceMode === 'audit' && audits.length > 0 && (
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '6px' }}>
                    SELECT AUDIT
                  </label>
                  <select
                    value={selectedAuditId}
                    onChange={(e) => handleAuditSelect(e.target.value)}
                    style={{
                      width: '100%',
                      background: '#040711',
                      border: '1px solid #1e293b',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      fontSize: '13px',
                      color: '#ffffff',
                      outline: 'none',
                    }}
                  >
                    {audits.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.clientName} ({a.websiteUrl})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {sourceMode === 'audit' && audits.length === 0 && (
                <div style={{ padding: '10px 14px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.25)', borderRadius: '8px', color: '#fca5a5', fontSize: '12px' }}>
                  No existing audits found. Enter details manually below or switch to "From Keyword Project".
                </div>
              )}

              {/* Keyword project selector if keyword mode */}
              {sourceMode === 'keyword' && kwProjects.length > 0 && (
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '6px' }}>
                    SELECT KEYWORD RESEARCH PROJECT
                  </label>
                  <select
                    value={selectedKwId}
                    onChange={(e) => handleKwSelect(e.target.value)}
                    style={{
                      width: '100%',
                      background: '#040711',
                      border: '1px solid #1e293b',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      fontSize: '13px',
                      color: '#ffffff',
                      outline: 'none',
                    }}
                  >
                    {kwProjects.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.clientName} ({p.websiteUrl})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {sourceMode === 'keyword' && kwProjects.length === 0 && (
                <div style={{ padding: '10px 14px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.25)', borderRadius: '8px', color: '#fca5a5', fontSize: '12px' }}>
                  No existing keyword research projects found. Enter details manually below or switch to "From Audit".
                </div>
              )}

              {/* Client Name */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '6px' }}>
                  CLIENT / BUSINESS NAME
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Apex Digital Solutions"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={{
                    width: '100%',
                    background: '#040711',
                    border: '1px solid #1e293b',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    fontSize: '13px',
                    color: '#ffffff',
                    outline: 'none',
                  }}
                />
              </div>

              {/* Website URL */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '6px' }}>
                  WEBSITE URL
                </label>
                <input
                  type="url"
                  required
                  placeholder="https://example.com"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  style={{
                    width: '100%',
                    background: '#040711',
                    border: '1px solid #1e293b',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    fontSize: '13px',
                    color: '#ffffff',
                    outline: 'none',
                  }}
                />
              </div>

              {/* Primary Location */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '6px' }}>
                  PRIMARY LOCATION / CITY
                </label>
                <input
                  type="text"
                  placeholder="e.g. Sydney or Multan"
                  value={loc}
                  onChange={(e) => setLoc(e.target.value)}
                  style={{
                    width: '100%',
                    background: '#040711',
                    border: '1px solid #1e293b',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    fontSize: '13px',
                    color: '#ffffff',
                    outline: 'none',
                  }}
                />
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px' }}>
                <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" icon={<Plus size={16} />}>
                  Create Plan
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
